<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1>About the GHG Calculator for Municipal Projects</h1>
        <p>
          The
          <span class="font-weight-bold"
            >GHG Calculator for Municipal Projects</span
          >
          is a free tool for integrating climate considerations into all
          municipal decision-making processes. From improving the insulation at
          your local ice rink to building new roads, the calculator can be used
          to estimate how much greenhouse gas a project or operational decision
          will emit or save.
        </p>
        <p class="mt-4">
          The resulting GHG estimates draw on data from Calgary, Canmore,
          Durham, Fredericton, Hamilton, Mississauga, Ottawa, Saint John, Saskatoon, Vaughan, Vernon, and West Vancouver,
          and can be used to approximate emissions for comparable municipalities
          in Alberta, BC, Ontario, and New Brunswick. When using the GHG
          Calculator, you will be prompted to select one of the existing
          municipalities to conduct your calculations. This enables all
          municipalities to try out the tool for free, and provides an
          approximation of the emissions associated with various projects and
          policies in comparable municipalities.
        </p>
        <p class="mt-4">
          If you wish to integrate the calculator into municipal operations, SSG
          can customize it to reflect your municipality's energy mix and
          emissions factors, and tailor it to any other specific needs. Learn
          more about our customization services.
        </p>
        <v-btn
          color="success"
          class="mt-4 mb-8"
          :to="localeRoute({ name: 'customization' })"
          >Learn more about our customization services</v-btn
        >
        <p class="text-subtitle-2">
          Current Version: {{ $t(`appInfo.version`) }}
        </p>
        <h2 class="mt-8">User Guide</h2>
        <p>The User Guide details how to use the GHG Calculator.</p>
        <v-btn
          color="success"
          class="mt-4 mb-8 text-white"
          href="https://ghg-calculator.ssg.coop/documentation/GHG_Calculator_User_Guide.pdf"
          target="_blank"
          append-icon="$filePdf"
          >Download the User Guide</v-btn
        >
        <h2>Tutorial Videos</h2>
        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="4"
            v-for="video in videos"
            :key="video.id"
          >
            <v-card class="h-100">
              <v-card-title>{{ video.title }}</v-card-title>
              <v-card-text>
                <LiteYouTubeEmbed
                  :id="video.id"
                  :title="video.title"
                ></LiteYouTubeEmbed>
                <p class="mt-2">{{ video.description }}</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>
<script setup lang="ts">
import LiteYouTubeEmbed from "vue-lite-youtube-embed";
import "vue-lite-youtube-embed/style.css";

const videos = [
  {
    id: "KQa0w90Cjd4",
    title: "About the GHG Calculator",
    description: `A brief introduction to the GHG Calculator, its intended purpose, and some of its features.`,
  },
  {
    id: "1uIi-kHspH0",
    title: "Installing the Add-In",
    description: `How to download and install the Excel add-in.`,
  },
  {
    id: "_ufr1I5sIFA",
    title: "Introduction to the Add-In Interface",
    description: `A brief tour of the GHG Calculator’s user interface.`,
  },
  {
    id: "c_P8uz2xWeI",
    title: "Create a new project",
    description: `How to create a new project.`,
  },
  {
    id: "tGnr9IJKPYE",
    title: "Create a new action",
    description: `How to create a new action within a project.`,
  },
  {
    id: "kRlflk2feXE",
    title: "Calculator Worksheets Overview",
    description: `An overview of the different types of worksheets the GHG Calculator produces.`,
  },
  {
    id: "IC_O6PldH88",
    title: "Registering for an Online Account",
    description: `How to register for a free online account.`,
  },
  {
    id: "CzvY-u33gAc",
    title: "Project Sharing",
    description: `How to share your project with other users.`,
  },
  {
    id: "FpohdHCFuyQ",
    title: "About SSG",
    description: `About Sustainability Solutions Group.`,
  },
];

useSeoMeta({
  description: "GHG Calculator for Municipal Projects - Documentation",
  ogTitle: "Documentation - GHG Calculator for Municipal Projects",
  ogDescription: "GHG Calculator for Municipal Projects - Documentation",
  ogImage: "/images/ghg_calculator_icon_250.png",
  ogUrl: "https://ghg-calculator.ssg.coop",
  twitterTitle: "Documentation - GHG Calculator for Municipal Projects",
  twitterDescription: "GHG Calculator for Municipal Projects - Documentation",
  twitterImage: "/images/ghg_calculator_icon_250.png",
  twitterCard: "summary",
});

useHead({
  title: "Documentation",
  htmlAttrs: {
    lang: "en",
  },
});
</script>
<style lang="scss">
@use "./settings";
</style>
